/**
 * What a sprite is doing at a sim time, read off the event log: idling (before the snap and
 * whenever it stands still), running (on a move leg that goes somewhere), or holding a frame (from
 * the tackle or sack it is part of, and everyone from the whistle). Presentation only: it picks an
 * animation, never a position. No Phaser import.
 */
import type { PlayEvent, UnitId } from '@tdmons/sim';

export type Pose = 'idle' | 'run' | 'hold';

export interface Poses {
  /** Sim time of the whistle: everyone holds from here. */
  whistle: number;
  /** Per unit: when it froze in a tackle or sack, and its move legs as [start, end]. */
  units: ReadonlyMap<UnitId, { holdFrom: number; legs: readonly [number, number][] }>;
}

export function buildPoses(events: readonly PlayEvent[]): Poses {
  const units = new Map<UnitId, { holdFrom: number; legs: [number, number][] }>();
  const of = (u: UnitId) => {
    let p = units.get(u);
    if (!p) units.set(u, (p = { holdFrom: Infinity, legs: [] }));
    return p;
  };
  const hold = (u: UnitId, t: number) => {
    const p = of(u);
    p.holdFrom = Math.min(p.holdFrom, t);
  };
  let whistle = Infinity;
  for (const e of events) {
    switch (e.type) {
      case 'move':
        if (e.from[0] !== e.to[0] || e.from[1] !== e.to[1]) {
          of(e.unit).legs.push([e.t, e.t + e.duration]);
        }
        break;
      case 'tackle':
        hold(e.unit, e.t);
        for (const b of e.by) hold(b, e.t);
        break;
      case 'sack':
        hold(e.unit, e.t);
        hold(e.by, e.t);
        break;
      case 'whistle':
        whistle = e.t;
        break;
      default:
        break;
    }
  }
  return { whistle, units };
}

export function poseAt(poses: Poses, unit: UnitId, t: number): Pose {
  const p = poses.units.get(unit);
  if (t >= poses.whistle || (p !== undefined && t >= p.holdFrom)) return 'hold';
  if (p?.legs.some(([a, b]) => t >= a && t < b)) return 'run';
  return 'idle';
}
